import axios from "axios";
import { ProductConstraints } from "@vp/hamilton-service-clients/dist/src/types/Constraints";

import { LowestPrice } from "@vp/hamilton-service-clients/dist/src/types/Pricing";
import { BFFCache } from "./cache/BFFCache";
import { Logger } from "../logging/logger";
import PageConfig from "../utils/PageConfig";
import { MerchandisingProductView, SiteflowResponse } from "../types/ContentfulTypes";
import RangeType from "../types/RangeTypes";
import {
  BffDebugData,
  BFFRequestParams,
  CompatibleOptionsResponse,
} from "../types/BFFServiceTypes";
import { fireSiteflowExperiments } from "../utils/fireSiteflowExperimentImpression";

// <link rel='preload' type='application/json'> requires
// an explicit '*/*' (any) accept header
const PRELOADABLE_CONFIG = { headers: { accept: "*/*" } };
export const BFF_BASE_URL = "https://product-pages-v2-bff.prod.merch.vpsvc.com";

const getCompatibilityAndPricing = async (
  requestUrl: string,
  isSSR?: boolean
): Promise<CompatibleOptionsResponse | undefined> => {
  let response: CompatibleOptionsResponse | undefined = undefined;
  try {
    const axiosResponse = await axios.get(requestUrl, PRELOADABLE_CONFIG);
    response = axiosResponse.data;
    Logger.instance.info("Call to BFF successful", {
      isSSR,
      requestUrl,
    });
  } catch (error) {
    Logger.instance.error("Call to BFF failed", { isSSR, requestUrl, error });
  }

  return response;
};

export const createBffRequest = (props: {
  tenant: string;
  locale: string;
  productKey: string;
  selections: Record<string, string>;
  productPageOptions: string[];
  quantities: number[];
  pricingContext?: string;
  version?: number;
  mpvId?: string;
  currentQuantity?: number;
  optimizelyEndUserId: string;
  requestor?: string;
  isDebug?: boolean;
}) => {
  const {
    tenant,
    locale,
    productKey,
    selections,
    productPageOptions,
    quantities,
    pricingContext,
    version,
    mpvId,
    currentQuantity,
    optimizelyEndUserId,
    requestor,
    isDebug,
  } = props;

  let queryString =
    `selections=${encodeURIComponent(JSON.stringify(selections))}` +
    `&productPageOptions=${encodeURIComponent(JSON.stringify(productPageOptions))}` +
    `&quantities=${encodeURIComponent(JSON.stringify(quantities))}` +
    `&version=${version || ""}` +
    "&applyProductConstraints=true" +
    `&mpvId=${mpvId ? mpvId : ""}` +
    `&requestor=${requestor || PageConfig.requestor}` +
    `&currentQuantity=${currentQuantity || ""}` +
    `&optimizelyEndUserId=${optimizelyEndUserId || ""}` +
    (isDebug ? `&debug=true` : ``);
  if (pricingContext) {
    queryString += `&pricingContext=${pricingContext}`;
  }

  return `${BFF_BASE_URL}/v1/compatibility-pricing/${tenant}/${locale}/${productKey}?${queryString}`;
};

const callBFF = async (props: {
  locale: string;
  productKey: string;
  selections: Record<string, string>;
  productPageOptions: string[];
  quantities: number[];
  pricingContext?: string;
  version?: number;
  isSSR?: boolean;
  mpvId?: string;
  currentQuantity?: number;
  optimizelyEndUserId: string;
  isDebug?: boolean;
}): Promise<
  | {
      compatibleValues: Record<string, string[]>;
      compatibleRanges: Record<string, RangeType[]>;
      compatibleQuantities: number[];
      prices: Record<string, LowestPrice>;
      version?: number;
      productConstraints?: ProductConstraints[];
      bffRequestParams?: BFFRequestParams;
      bffRequestUrl?: string;
      mpv?: MerchandisingProductView;
      debug?: BffDebugData;
      multisiteflow?: { flows: SiteflowResponse[] };
    }
  | undefined
> => {
  const {
    locale,
    productKey,
    selections,
    productPageOptions,
    quantities,
    pricingContext,
    version,
    isSSR,
    mpvId,
    currentQuantity,
    optimizelyEndUserId,
    isDebug,
  } = props;

  const cacheKey = {
    productKey,
    locale,
    selections,
    selectedQuantity: currentQuantity,
    pricingContext: pricingContext || "",
    optimizelyEndUserId,
  };

  const bffCache = BFFCache.getInstance();
  let bffData = bffCache.getData(cacheKey) as CompatibleOptionsResponse | undefined;
  const bffRequestParams = {
    tenant: PageConfig.tenant,
    locale,
    productKey,
    selections,
    pricingContext,
    productPageOptions,
    version,
    quantities,
    mpvId,
    currentQuantity,
    optimizelyEndUserId,
    isDebug,
  };

  const bffRequestUrl = createBffRequest(bffRequestParams);

  if (!bffData) {
    bffData = await getCompatibilityAndPricing(bffRequestUrl, isSSR);
    if (bffData) bffCache.addData({ ...cacheKey, data: bffData });
  }

  if (bffData) {
    let prices: Record<string, LowestPrice> = {};
    const {
      compatibleOptions,
      compatibleRanges,
      compatibleQuantities,
      pricing,
      productConstraints,
      mpv,
      debug,
      siteflow,
    } = bffData;

    for (const simplifiedFlow of siteflow?.flows || []) {
      // TODO: This may not work as expected: Check when/why it could be undefined
      if (mpvId !== undefined) {
        await fireSiteflowExperiments(simplifiedFlow, mpvId);
      }
    }

    if (pricing) {
      prices = pricing.default;
    }

    return {
      compatibleValues: compatibleOptions,
      compatibleRanges,
      compatibleQuantities,
      prices,
      version,
      productConstraints,
      bffRequestParams,
      bffRequestUrl,
      mpv,
      debug,
      multisiteflow: siteflow,
    };
  }

  return undefined;
};

const getAccessoryMpvs = async (
  productKey: string,
  version: number,
  locale: string,
  tenant: string,
  fetchPricing: boolean,
  pricingContext?: string
) => {
  if (!productKey || !version || !locale || !tenant) {
    Logger.instance.error("Failed to get accessory mpvs: missing request parameters.");
    return [];
  }

  const requestUrl = `${BFF_BASE_URL}/v1/accessory/${tenant}/${locale}/${productKey}/${version}?requestor=${PageConfig.requestor}&fetchPricing=${fetchPricing}&pricingContext=${pricingContext}&debug=true`;

  let response;
  try {
    const axiosResponse = await axios.get(requestUrl);
    response = axiosResponse?.data;
  } catch (error) {
    Logger.instance.error("Failed to get accessory mpvs", {
      requestUrl,
      error,
    });
  }

  return response;
};

export { getCompatibilityAndPricing, callBFF, getAccessoryMpvs };
